import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CampaignsAPI from '../../api/campaigns';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { CAMPAIGNS_EVENTS } from '../../helper/AnalyticsHelper/events';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isExporting: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getCampaigns:
    _state =>
    (campaignType, inboxChannelTypes = null) => {
      let filteredRecords = _state.records.filter(
        record => record.campaign_type === campaignType
      );

      if (inboxChannelTypes && Array.isArray(inboxChannelTypes)) {
        filteredRecords = filteredRecords.filter(record => {
          return (
            record.inbox &&
            inboxChannelTypes.includes(record.inbox.channel_type)
          );
        });
      }

      return filteredRecords.sort((a1, a2) => a1.id - a2.id);
    },
  getSMSCampaigns: (_state, _getters) => {
    const smsChannelTypes = [INBOX_TYPES.SMS, INBOX_TYPES.TWILIO];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONE_OFF, smsChannelTypes);
  },
  getWhatsAppCampaigns: (_state, _getters) => {
    const whatsappChannelTypes = [INBOX_TYPES.WHATSAPP];
    return _getters.getCampaigns(CAMPAIGN_TYPES.ONE_OFF, whatsappChannelTypes);
  },
  getLiveChatCampaigns: (_state, _getters) => {
    // Live chat campaigns are disabled
    return [];
  },
  getAllCampaigns: _state => {
    return _state.records;
  },
};

export const actions = {
  get: async function getCampaigns({ commit }) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
    try {
      const response = await CampaignsAPI.get();
      commit(types.SET_CAMPAIGNS, response.data);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
    }
  },
  create: async function createCampaign({ commit }, campaignObj) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: true });
    try {
      const response = await CampaignsAPI.create(campaignObj);
      commit(types.ADD_CAMPAIGN, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      const response = await CampaignsAPI.update(id, updateObj);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.UPDATE_CAMPAIGN);
      commit(types.EDIT_CAMPAIGN, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: true });
    try {
      await CampaignsAPI.delete(id);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.DELETE_CAMPAIGN);
      commit(types.DELETE_CAMPAIGN, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: false });
    }
  },
  pause: async ({ commit }, id) => {
    try {
      await CampaignsAPI.pause(id);
      commit(types.EDIT_CAMPAIGN_ATTRIBUTES, {
        id,
        campaign_status: 'paused',
        latest_execution_status: 'paused',
      });
    } catch (error) {
      throw new Error(error);
    }
  },
  resume: async ({ commit }, id) => {
    try {
      await CampaignsAPI.resume(id);
      commit(types.EDIT_CAMPAIGN_ATTRIBUTES, {
        id,
        campaign_status: 'active',
        latest_execution_status: 'running',
      });
    } catch (error) {
      throw new Error(error);
    }
  },
  stop: async ({ commit }, id) => {
    try {
      await CampaignsAPI.stop(id);
      commit(types.EDIT_CAMPAIGN_ATTRIBUTES, {
        id,
        campaign_status: 'stopped',
        latest_execution_status: 'stopped',
      });
    } catch (error) {
      throw new Error(error);
    }
  },
  exportReport: async (
    { commit, state: campaignState },
    { campaignId, executionId }
  ) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isExporting: true });
    try {
      // eslint-disable-next-line no-console
      console.log('Starting export for campaign:', campaignId);

      const response = await CampaignsAPI.exportReport(campaignId, executionId);

      // eslint-disable-next-line no-console
      console.log('Export response:', response);

      // Find the campaign to get its name
      const campaign = campaignState.records.find(c => c.id === campaignId);
      const campaignName = campaign ? campaign.title : `campaign_${campaignId}`;

      // Sanitize filename - remove special characters and replace spaces with underscores
      const sanitizedName = campaignName
        .replace(/[^a-zA-Z0-9\s-_]/g, '')
        .replace(/\s+/g, '_')
        .toLowerCase();

      // Create a download link for the CSV
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${sanitizedName}_report_${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      AnalyticsHelper.track(CAMPAIGNS_EVENTS.EXPORT_REPORT);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Export error:', error);
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isExporting: false });
    }
  },
};

export const mutations = {
  [types.SET_CAMPAIGN_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.ADD_CAMPAIGN]: MutationHelpers.create,
  [types.SET_CAMPAIGNS]: MutationHelpers.set,
  [types.EDIT_CAMPAIGN]: MutationHelpers.update,
  [types.EDIT_CAMPAIGN_ATTRIBUTES]: MutationHelpers.updateAttributes,
  [types.DELETE_CAMPAIGN]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
